/**
 * ReferralPage — Partner-only referral dashboard.
 * Only visible in the sidebar if user.isReferralPartner === true.
 *
 * Shows:
 *  - Referral code (copy to clipboard)
 *  - Referral link
 *  - Commission % and bonus % for buyers
 *  - Unpaid balance
 *  - Lifetime stats (total earned, total referrals, total volume)
 *  - Transaction history table
 */

import React, { useEffect, useState } from 'react';
import Layout from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import { referralAPI, ReferralStats } from '../services/api';

const ReferralPage: React.FC = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState<ReferralStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [codeCopied, setCodeCopied] = useState(false);

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await referralAPI.getStats();
      if (result.success && result.data) {
        setStats(result.data);
      } else {
        setError(result.error || 'Failed to load referral stats');
      }
    } catch (err) {
      setError('Failed to load referral stats');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text).then(() => {
      setCodeCopied(true);
      setTimeout(() => setCodeCopied(false), 2000);
    });
  };

  // If user is somehow not a partner (shouldn't happen due to route guard), show message
  if (!user?.isReferralPartner) {
    return (
      <Layout>
        <div className="p-8 text-center text-gray-400">
          <p>This page is not available.</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-4 md:p-6 lg:p-8 max-w-5xl mx-auto">

        {/* Header */}
        <div className="mb-6">
          <h1 className="text-xl md:text-2xl font-bold text-white">Referral Dashboard</h1>
          <p className="text-sm text-gray-400 mt-1">
            Share your code — referred users get bonus GB, you earn commission.
          </p>
        </div>

        {loading && (
          <div className="text-center py-16 text-gray-400">
            <p>Loading...</p>
          </div>
        )}

        {error && (
          <div className="bg-red-900/30 border border-red-500/30 rounded-xl p-4 text-red-400">
            {error}
          </div>
        )}

        {!loading && !error && stats && (
          <>
            {/* Code + Link cards */}
            <div className="grid md:grid-cols-2 gap-4 mb-6">

              {/* Referral Code */}
              <div className="bg-gray-800 rounded-xl p-5 border border-gray-700">
                <p className="text-xs text-gray-400 mb-2 font-medium uppercase tracking-wide">Your Referral Code</p>
                <div className="flex items-center gap-3">
                  <span className="text-2xl font-bold text-white tracking-widest">
                    {stats.partner.referralCode}
                  </span>
                  <button
                    onClick={() => copyToClipboard(stats.partner.referralCode)}
                    className="ml-auto px-3 py-1.5 bg-gray-700 hover:bg-gray-600 rounded-lg text-xs text-gray-300 transition-all"
                  >
                    {codeCopied ? 'Copied!' : 'Copy'}
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  Users enter this at checkout to get +{stats.partner.bonusGbPercentage}% bonus GB
                </p>
              </div>

              {/* How it works */}
              <div className="bg-gray-800 rounded-xl p-5 border border-gray-700">
                <p className="text-xs text-gray-400 mb-2 font-medium uppercase tracking-wide">How It Works</p>
                <p className="text-sm text-gray-300">
                  Users enter your code at checkout to receive bonus GB. You earn commission on every purchase.
                </p>
              </div>
            </div>

            {/* Stats overview */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <div className="bg-gray-800 rounded-xl p-4 border border-gray-700">
                <p className="text-xs text-gray-400 mb-1">Unpaid Balance</p>
                <p className="text-xl font-bold text-green-400">
                  ${stats.partner.balance.toFixed(2)}
                </p>
              </div>
              <div className="bg-gray-800 rounded-xl p-4 border border-gray-700">
                <p className="text-xs text-gray-400 mb-1">Total Earned</p>
                <p className="text-xl font-bold text-white">
                  ${stats.stats.totalEarned.toFixed(2)}
                </p>
              </div>
              <div className="bg-gray-800 rounded-xl p-4 border border-gray-700">
                <p className="text-xs text-gray-400 mb-1">Conversions</p>
                <p className="text-xl font-bold text-white">
                  {stats.stats.totalReferrals}
                </p>
              </div>
              <div className="bg-gray-800 rounded-xl p-4 border border-gray-700">
                <p className="text-xs text-gray-400 mb-1">Volume Generated</p>
                <p className="text-xl font-bold text-white">
                  ${stats.stats.totalVolumeUsd.toFixed(2)}
                </p>
              </div>
            </div>

            {/* Deal details */}
            <div className="bg-gray-800 rounded-xl p-5 border border-gray-700 mb-6">
              <h2 className="text-sm font-semibold text-white mb-3">Your Deal</h2>
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-blue-600/20 flex items-center justify-center flex-shrink-0">
                    <svg className="w-5 h-5 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-white font-semibold">{stats.partner.commissionPercentage}% Commission</p>
                    <p className="text-xs text-gray-400">You earn {stats.partner.commissionPercentage}% of every purchase made with your code</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-green-600/20 flex items-center justify-center flex-shrink-0">
                    <svg className="w-5 h-5 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-white font-semibold">+{stats.partner.bonusGbPercentage}% Bonus GB for Buyers</p>
                    <p className="text-xs text-gray-400">Users who use your code get {stats.partner.bonusGbPercentage}% extra GB on their purchase</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Transaction History */}
            <div className="bg-gray-800 rounded-xl border border-gray-700">
              <div className="p-5 border-b border-gray-700">
                <h2 className="text-sm font-semibold text-white">Transaction History</h2>
              </div>
              {stats.transactions.length === 0 ? (
                <div className="p-8 text-center text-gray-400 text-sm">
                  No transactions yet. Share your code to get started!
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-700">
                        <th className="text-left text-xs text-gray-400 font-medium px-5 py-3">Date</th>
                        <th className="text-left text-xs text-gray-400 font-medium px-5 py-3">Buyer</th>
                        <th className="text-right text-xs text-gray-400 font-medium px-5 py-3">Purchase</th>
                        <th className="text-right text-xs text-gray-400 font-medium px-5 py-3">GB Credited</th>
                        <th className="text-right text-xs text-gray-400 font-medium px-5 py-3">Your Commission</th>
                      </tr>
                    </thead>
                    <tbody>
                      {stats.transactions.map((tx) => (
                        <tr key={tx.id} className="border-b border-gray-700/50 hover:bg-gray-700/30 transition-colors">
                          <td className="px-5 py-3 text-gray-300 whitespace-nowrap">
                            {new Date(tx.date).toLocaleDateString()}
                          </td>
                          <td className="px-5 py-3 text-gray-300">
                            {/* Mask email for privacy — show only first 2 chars + domain */}
                            {maskEmail(tx.buyerEmail)}
                          </td>
                          <td className="px-5 py-3 text-white text-right font-medium">
                            ${tx.purchaseUsd.toFixed(2)}
                          </td>
                          <td className="px-5 py-3 text-blue-400 text-right">
                            {tx.gbAmount.toFixed(0)} GB
                            {tx.bonusGbPct > 0 && (
                              <span className="text-green-400 text-xs ml-1">(+{tx.bonusGbPct}%)</span>
                            )}
                          </td>
                          <td className="px-5 py-3 text-green-400 text-right font-semibold">
                            +${tx.commissionUsd.toFixed(2)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Payout info */}
            <div className="mt-4 p-4 bg-gray-800/50 rounded-xl border border-gray-700 text-xs text-gray-400">
              <strong className="text-gray-300">Payouts:</strong> Contact us on{' '}
              <a
                href="https://t.me/NetloxProxies"
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-400 hover:underline"
              >
                Telegram
              </a>{' '}
              to request a payout of your balance. Minimum payout: $20.
            </div>
          </>
        )}
      </div>
    </Layout>
  );
};

/**
 * Masks an email for privacy in the transactions table.
 * e.g. "john.doe@gmail.com" → "jo***@gmail.com"
 */
function maskEmail(email: string): string {
  if (!email || !email.includes('@')) return email;
  const [local, domain] = email.split('@');
  const visible = local.slice(0, 2);
  return `${visible}***@${domain}`;
}

export default ReferralPage;
